import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import { Formik } from 'formik';
import * as yup from 'yup';
import { TextFormField } from 'lib/form/TextFormField';
import { isNumber, isString } from 'lib/typeInference';
import { useCurrencyString } from 'lib/useCurrencyString';
import { LoadingButton } from 'ui/LoadingButton';
import { BasketGiftCardModal } from './BasketGitfCardOverpaymentModal';

const PREFIX = 'BasketGiftCard';

const classes = {
    applyButton: `${PREFIX}applyButton`
};

const StyledDiv = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',

    [`& .${classes.applyButton}`]: {
        marginLeft: theme.spacing(1),
        height: '40px'
    }
}));

interface IProps {
    giftCardValue?: string;
    onGiftCardApply: (giftCardNumber: string | undefined) => void;
    error?: string;
    balance?: number;
    loading?: boolean;
    showOverpayment?: boolean;
    orderBalance?: number;
    reset: () => void;
    resetErrors: () => void;
    onAcceptOverpayment: () => void;
}

export const BasketGiftCard: React.FC<React.PropsWithChildren<IProps>> = ({
    giftCardValue,
    error,
    balance,
    onGiftCardApply,
    onAcceptOverpayment,
    showOverpayment,
    orderBalance,
    loading,
    reset,
    resetErrors
}) => {
    const getCurrencyString = useCurrencyString();
    const { t } = useTranslation();
    const validation = React.useMemo(
        () =>
            yup.object().shape({
                giftCardNumber: yup.string().required(t('CHECKOUT_GIFTCARD_INVALID_FORMAT'))
            }),
        [t]
    );
    const initialFields: { giftCardNumber: string } = React.useMemo(
        () => ({
            giftCardNumber: giftCardValue || ''
        }),
        [giftCardValue]
    );
    const handleSubmit = React.useCallback(
        ({ giftCardNumber }: { giftCardNumber: string }) => {
            resetErrors();
            if (onGiftCardApply) {
                onGiftCardApply(giftCardNumber.replace(/\s/g, ''));
            }
        },
        [onGiftCardApply, resetErrors]
    );
    const handleReset = React.useCallback(
        (resetForm: () => void) => () => {
            resetForm();
            reset();
            resetErrors();
        },
        [reset, resetErrors]
    );
    const isInvalid = React.useMemo(() => error, [error]);
    const isSelected = React.useMemo(() => !isInvalid && giftCardValue, [giftCardValue, isInvalid]);

    return (
        <Formik initialValues={initialFields} validationSchema={validation} onSubmit={handleSubmit}>
            {({ submitForm, resetForm, values }) => (
                <StyledDiv>
                    <Box flex={1}>
                        <TextFormField
                            size="small"
                            name="giftCardNumber"
                            label=""
                            placeholder={t('CHECKOUT_GIFTCARD_ENTRY_TEXT')}
                            disabled={!!isSelected || loading}
                            trimWhiteSpaces="all"
                        />
                        {isNumber(balance) && (
                            <Typography variant="caption">
                                {t('CHECKOUT_GIFTCARD_REMAINING_BALANCE', {
                                    balance: getCurrencyString(balance)
                                })}
                            </Typography>
                        )}
                        {/* TODO: do we want to do something about that? */}
                        {isString(error) && values.giftCardNumber.trim().length && (
                            <Typography variant="caption" color="error">
                                {error}
                            </Typography>
                        )}
                    </Box>
                    {isSelected ? (
                        <Button
                            onClick={handleReset(resetForm)}
                            className={classes.applyButton}
                            variant="contained"
                            color="primary"
                        >
                            {t('CHECKOUT_GIFTCARD_CLEAR_BTN')}
                        </Button>
                    ) : (
                        <LoadingButton
                            onClick={submitForm}
                            className={classes.applyButton}
                            variant="contained"
                            loading={loading}
                            color="primary"
                            disabled={!values.giftCardNumber.trim() || loading}
                        >
                            {t('CHECKOUT_GIFTCARD_APPLY_BTN')}
                        </LoadingButton>
                    )}
                    <BasketGiftCardModal
                        open={!!showOverpayment}
                        onAccept={onAcceptOverpayment}
                        onCancel={handleReset(resetForm)}
                        giftCardNumber={values.giftCardNumber}
                        gitcardBalance={balance}
                        orderBalance={orderBalance}
                    />
                </StyledDiv>
            )}
        </Formik>
    );
};
